import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import inspoSpotsIcon from '@/assets/inspo-spots-icon.svg';
import CustomImage from '@/components/CustomImage/CustomImage';
import {
  PlaceSearchRowTypeIcon,
  type PlaceSearchRowSlotKind,
} from '@/components/PlaceSearchAutocomplete/PlaceSearchRowTypeIcon';

export type SocialImportReviewPlace = {
  id: string;
  title: string;
  subtitle: string;
  imageUrl?: string | null;
  iconKind: PlaceSearchRowSlotKind;
  disabled?: boolean;
  statusLine?: string | null;
  /** Import-docs review: bookable pill on the row (Figma). */
  bookableChip?: string | null;
  /** Import-docs review: flight/transport row without selection control. */
  isTravel?: boolean;
  /** Trip import: low-confidence hint shown below the row (not in statusLine). */
  matchReason?: string | null;
  matchConfidence?: 'high' | 'low' | 'none';
  /** Trip import: excerpt from the document; review-only, never saved. */
  sourceQuote?: string | null;
  /** Non-travel import row can be looked up, or looked up again, as a Google place. */
  canFindPlace?: boolean;
  /** Label for the lookup action. Defaults to "Find this place". */
  findPlaceLabel?: string;
  /** Import review: place notes saved on create (editable in expanded panel). */
  editableNotes?: string;
};

export type SocialImportReviewSection = {
  key: string;
  heading?: string | null;
  subheading?: string | null;
  /** Optional day- or section-level narrative (e.g. itinerary import day notes). */
  description?: string | null;
  places: SocialImportReviewPlace[];
};

export type SocialImportPlaceReviewVariant = 'social' | 'importDocs';

export interface SocialImportPlaceReviewProps {
  variant?: SocialImportPlaceReviewVariant;
  previewLabel: string;
  previewBadge?: string;
  title: string;
  onTitleChange: (value: string) => void;
  titleInputLabel: string;
  coverImageUrl?: string | null;
  fallbackCoverUrls?: string[];
  totalPlaceCount: number;
  sections: SocialImportReviewSection[];
  selectedIds: Set<string>;
  onTogglePlace: (id: string) => void;
  onSelectAllToggle: () => void;
  isBusy?: boolean;
  className?: string;
  /** Trip import: open place search for an unmatched row. */
  onFindPlace?: (id: string) => void;
  /** Import review: update draft place notes. */
  onPlaceNotesChange?: (id: string, notes: string) => void;
  /** Import review: chevron expand/collapse (e.g. analytics). */
  onPlaceDetailsExpandedChange?: (id: string, expanded: boolean) => void;
  /** Social inspo review — inline Change link and caption excerpt inside the card. */
  socialImportPlaceRows?: boolean;
  /** Label for collapsed source quote (social: "From the caption"). */
  sourceExcerptLabel?: string;
}

export function SocialImportPlaceReview({
  variant = 'social',
  previewLabel,
  previewBadge,
  title,
  onTitleChange,
  titleInputLabel,
  coverImageUrl,
  fallbackCoverUrls = [],
  totalPlaceCount,
  sections,
  selectedIds,
  onTogglePlace,
  onSelectAllToggle,
  isBusy = false,
  className = '',
  onFindPlace,
  onPlaceNotesChange,
  onPlaceDetailsExpandedChange,
  socialImportPlaceRows = false,
  sourceExcerptLabel = 'Excerpt from document',
}: SocialImportPlaceReviewProps) {
  const isImportDocs = variant === 'importDocs';
  const [expandedPlaceIds, setExpandedPlaceIds] = useState<Set<string>>(() => new Set());
  const selectableIds = useMemo(() => {
    const ids: string[] = [];
    sections.forEach((section) => {
      section.places.forEach((place) => {
        if (!place.disabled) ids.push(place.id);
      });
    });
    return ids;
  }, [sections]);

  const allSelectableSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const placeRowCount = useMemo(
    () => sections.reduce((count, section) => count + section.places.length, 0),
    [sections],
  );

  const spotsScrollRef = useRef<HTMLDivElement | null>(null);
  const spotsScrollSentinelRef = useRef<HTMLDivElement | null>(null);
  const [showSpotsScrollHint, setShowSpotsScrollHint] = useState(false);

  const updateSpotsScrollHint = useCallback(() => {
    const root = spotsScrollRef.current;
    const sentinel = spotsScrollSentinelRef.current;
    if (!root || !sentinel) return;

    const hasOverflow = root.scrollHeight > root.clientHeight + 2;
    if (!hasOverflow) {
      setShowSpotsScrollHint(false);
      return;
    }

    const rootRect = root.getBoundingClientRect();
    const sentinelRect = sentinel.getBoundingClientRect();
    const sentinelVisible = sentinelRect.bottom > rootRect.top && sentinelRect.top < rootRect.bottom;
    setShowSpotsScrollHint(!sentinelVisible);
  }, []);

  useEffect(() => {
    const root = spotsScrollRef.current;
    if (!root || placeRowCount === 0) {
      setShowSpotsScrollHint(false);
      return;
    }

    updateSpotsScrollHint();
    const raf = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(updateSpotsScrollHint);
    });

    const resizeObserver = new ResizeObserver(() => {
      updateSpotsScrollHint();
    });
    resizeObserver.observe(root);

    root.addEventListener('scroll', updateSpotsScrollHint, { passive: true });

    return () => {
      window.cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      root.removeEventListener('scroll', updateSpotsScrollHint);
    };
  }, [placeRowCount, selectedIds.size, updateSpotsScrollHint, sections]);

  const renderPlaceRow = useCallback(
    (place: SocialImportReviewPlace) => {
      const isSelected = selectedIds.has(place.id);
      const needsMatchReview = place.matchConfidence === 'low';
      const rowImageSrc =
        typeof place.imageUrl === 'string' && place.imageUrl.trim() ? place.imageUrl : undefined;

      return (
        <div
          key={place.id}
          className={`rounded-button bg-white py-1 ${
            needsMatchReview ? 'border-l-2 border-amber-400 pl-2' : 'border-l-2 border-transparent pl-0'
          }`}
        >
          <button
            type="button"
            disabled={place.disabled || isBusy}
            onClick={() => onTogglePlace(place.id)}
            className={`flex w-full items-center gap-4 border border-transparent px-0 py-2 text-left hover:border-transparent ${
              place.disabled ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            <div className="relative h-10 w-10 min-w-[40px] overflow-hidden rounded-lg bg-gray-200 [&>div]:h-full [&>div]:w-full [&>div]:min-h-0">
              <CustomImage
                src={rowImageSrc}
                alt=""
                fill
                sizes="40px"
                fallbackSrc="/images/default_post.png"
                lazy={false}
                style={{ objectFit: 'cover', objectPosition: 'center' }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-4 w-4 items-center justify-center text-[#252528]">
                  <PlaceSearchRowTypeIcon kind={place.iconKind} />
                </span>
                <p className="truncate font-manrope text-sm font-semibold tracking-[0.02em] text-[#252528]">
                  {place.title}
                </p>
              </div>
              <p className="truncate font-manrope text-xs font-normal tracking-[0.02em] text-[#a7a7ae]">
                {place.subtitle}
              </p>
              {place.statusLine ? (
                <p className="truncate font-manrope text-xs font-normal text-[#525257]">{place.statusLine}</p>
              ) : null}
            </div>
            <span
              className={`inline-flex h-6 w-6 min-w-[24px] items-center justify-center rounded-full ${
                isSelected ? 'bg-[#e3f0ad] text-[#252528]' : 'border border-[#e1e1e7] bg-white text-transparent'
              }`}
            >
              <CheckIcon sx={{ fontSize: 14 }} />
            </span>
          </button>
          {place.matchReason ? (
            <p className="pb-1 pl-14 pr-10 font-manrope text-xs leading-relaxed text-amber-900">
              {place.matchReason}
            </p>
          ) : null}
          {place.canFindPlace && onFindPlace ? (
            <button
              type="button"
              className="pb-2 pl-14 font-manrope text-xs font-semibold text-orange-600 disabled:opacity-40"
              disabled={isBusy}
              onClick={() => onFindPlace(place.id)}
            >
              {place.findPlaceLabel || 'Find this place'}
            </button>
          ) : null}
          {place.sourceQuote ? (
            <details className="group pb-2 pl-14 pr-4">
              <summary className="cursor-pointer list-none font-manrope text-xs font-medium text-[#525257] marker:content-none [&::-webkit-details-marker]:hidden">
                <span className="underline decoration-[#a7a7ae] underline-offset-2 group-open:no-underline">
                  Excerpt from document
                </span>
              </summary>
              <p className="mt-1 whitespace-pre-wrap font-manrope text-xs leading-relaxed text-[#525257]">
                {place.sourceQuote}
              </p>
            </details>
          ) : null}
        </div>
      );
    },
    [isBusy, onFindPlace, onTogglePlace, selectedIds],
  );

  const togglePlaceDetails = useCallback(
    (placeId: string) => {
      setExpandedPlaceIds((previous) => {
        const next = new Set(previous);
        const expanded = !next.has(placeId);
        if (expanded) {
          next.add(placeId);
        } else {
          next.delete(placeId);
        }
        onPlaceDetailsExpandedChange?.(placeId, expanded);
        return next;
      });
    },
    [onPlaceDetailsExpandedChange],
  );

  const renderImportDocsPlaceRow = useCallback(
    (place: SocialImportReviewPlace) => {
      const isSelected = selectedIds.has(place.id);
      const needsMatchReview = place.matchConfidence === 'low';
      const rowImageSrc =
        typeof place.imageUrl === 'string' && place.imageUrl.trim() ? place.imageUrl : undefined;
      const isTravel = place.isTravel === true;
      const canExpandDetails = !isTravel && Boolean(onPlaceNotesChange);
      const isExpanded = expandedPlaceIds.has(place.id);
      const notesFieldId = `trip-import-place-notes-${place.id}`;

      const rowMain = (
        <>
          {!isTravel ? (
            <span
              aria-hidden
              className="absolute left-[15px] top-1/2 size-[6px] -translate-y-1/2 rounded-[3px] bg-[#252528]"
            />
          ) : null}
          <button
            type="button"
            disabled={place.disabled || isBusy}
            onClick={() => onTogglePlace(place.id)}
            className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-not-allowed disabled:opacity-50"
          >
            {rowImageSrc ? (
              <div className="relative size-10 shrink-0 overflow-hidden rounded-lg bg-gray-200 [&>div]:h-full [&>div]:w-full">
                <CustomImage
                  src={rowImageSrc}
                  alt=""
                  fill
                  sizes="40px"
                  fallbackSrc="/images/default_post.png"
                  lazy={false}
                  style={{ objectFit: 'cover', objectPosition: 'center' }}
                />
              </div>
            ) : (
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#f3f3f3] text-[#252528]">
                <PlaceSearchRowTypeIcon kind={place.iconKind} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                {rowImageSrc ? (
                  <span className="inline-flex shrink-0 text-[#252528]">
                    <PlaceSearchRowTypeIcon kind={place.iconKind} />
                  </span>
                ) : null}
                <p className="truncate font-manrope text-sm font-semibold text-[#252528]">{place.title}</p>
              </div>
              {place.subtitle ? (
                <p className="truncate font-manrope text-xs text-[#525257]">{place.subtitle}</p>
              ) : null}
            </div>
          </button>
          {isTravel ? (
            <p className="shrink-0 font-manrope text-xs text-[#525257]">Travel · not a place</p>
          ) : place.bookableChip && canExpandDetails ? (
            <button
              type="button"
              disabled={isBusy}
              aria-expanded={isExpanded}
              aria-controls={notesFieldId}
              aria-label={isExpanded ? 'Hide place description' : 'Show place description'}
              onClick={() => togglePlaceDetails(place.id)}
              className="flex shrink-0 items-center gap-1 rounded-lg disabled:opacity-40"
            >
              <span className="rounded-lg bg-[#e3f0ad] px-2.5 py-1 font-manrope text-xs font-semibold text-[#252528]">
                {place.bookableChip}
              </span>
              <ChevronRightIcon
                sx={{
                  fontSize: 16,
                  color: '#252528',
                  transform: isExpanded ? 'rotate(90deg)' : undefined,
                  transition: 'transform 0.2s ease-out',
                }}
                aria-hidden
              />
            </button>
          ) : place.bookableChip ? (
            <span className="rounded-lg bg-[#e3f0ad] px-2.5 py-1 font-manrope text-xs font-semibold text-[#252528]">
              {place.bookableChip}
            </span>
          ) : canExpandDetails ? (
            <button
              type="button"
              disabled={isBusy}
              aria-expanded={isExpanded}
              aria-controls={notesFieldId}
              aria-label={isExpanded ? 'Hide place description' : 'Show place description'}
              onClick={() => togglePlaceDetails(place.id)}
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-[#252528] disabled:opacity-40"
            >
              <ChevronRightIcon
                sx={{
                  fontSize: 18,
                  transform: isExpanded ? 'rotate(90deg)' : undefined,
                  transition: 'transform 0.2s ease-out',
                }}
              />
            </button>
          ) : place.statusLine ? (
            <p className="max-w-[120px] shrink-0 truncate font-manrope text-xs text-[#525257]">
              {place.statusLine}
            </p>
          ) : null}
          {socialImportPlaceRows && place.canFindPlace && onFindPlace ? (
            <button
              type="button"
              className="shrink-0 font-manrope text-xs font-medium text-[#525257] hover:text-[#252528] disabled:opacity-40"
              disabled={isBusy}
              onClick={(event) => {
                event.stopPropagation();
                onFindPlace(place.id);
              }}
            >
              {place.findPlaceLabel || 'Find this place'}
            </button>
          ) : null}
          {!isTravel ? (
            <button
              type="button"
              disabled={place.disabled || isBusy}
              aria-label={isSelected ? 'Exclude place from import' : 'Include place in import'}
              aria-pressed={isSelected}
              onClick={() => onTogglePlace(place.id)}
              className={`inline-flex size-6 shrink-0 items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-50 ${
                isSelected ? 'bg-[#e3f0ad] text-[#252528]' : 'border border-[#e1e1e7] bg-white text-transparent'
              }`}
            >
              <CheckIcon sx={{ fontSize: 14 }} />
            </button>
          ) : null}
        </>
      );

      return (
        <div key={place.id} className="flex flex-col gap-1">
          {isTravel ? (
            <div
              className={`relative flex items-center gap-3 rounded-2xl border border-[#e1e1e7] bg-white px-4 py-3 ${
                needsMatchReview ? 'border-l-2 border-l-amber-400' : ''
              }`}
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                {rowImageSrc ? (
                  <div className="relative size-10 shrink-0 overflow-hidden rounded-lg bg-gray-200 [&>div]:h-full [&>div]:w-full">
                    <CustomImage
                      src={rowImageSrc}
                      alt=""
                      fill
                      sizes="40px"
                      fallbackSrc="/images/default_post.png"
                      lazy={false}
                      style={{ objectFit: 'cover', objectPosition: 'center' }}
                    />
                  </div>
                ) : (
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#f3f3f3] text-[#252528]">
                    <PlaceSearchRowTypeIcon kind={place.iconKind} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-manrope text-sm font-semibold text-[#252528]">{place.title}</p>
                  {place.subtitle ? (
                    <p className="truncate font-manrope text-xs text-[#525257]">{place.subtitle}</p>
                  ) : null}
                </div>
              </div>
              <p className="shrink-0 font-manrope text-xs text-[#525257]">Travel · not a place</p>
            </div>
          ) : (
            <div
              className={`relative flex flex-col rounded-2xl border border-[#e1e1e7] bg-white ${
                needsMatchReview ? 'border-l-2 border-l-amber-400' : ''
              }`}
            >
              <div className="relative flex items-center gap-2 px-4 py-3">{rowMain}</div>
              {socialImportPlaceRows && place.sourceQuote ? (
                <details className="group border-t border-[#e1e1e7] px-4 pb-3 pt-2">
                  <summary className="cursor-pointer list-none font-manrope text-xs font-medium text-[#525257] marker:content-none [&::-webkit-details-marker]:hidden">
                    <span className="underline decoration-[#a7a7ae] underline-offset-2 group-open:no-underline">
                      {sourceExcerptLabel}
                    </span>
                  </summary>
                  <p className="mt-1 whitespace-pre-wrap font-manrope text-xs leading-relaxed text-[#525257]">
                    {place.sourceQuote}
                  </p>
                </details>
              ) : null}
              {isExpanded && canExpandDetails ? (
                <div className="border-t border-[#e1e1e7] px-4 pb-3 pt-2">
                  <label
                    htmlFor={notesFieldId}
                    className="mb-1.5 block font-manrope text-xs font-medium text-[#525257]"
                  >
                    Description
                  </label>
                  <textarea
                    id={notesFieldId}
                    value={place.editableNotes ?? ''}
                    disabled={isBusy}
                    rows={3}
                    placeholder="Add details about this place"
                    onChange={(event) => onPlaceNotesChange?.(place.id, event.target.value)}
                    className="gt-keyboard-scroll w-full resize-y rounded-xl border border-[#e1e1e7] bg-white px-3 py-2 font-manrope text-xs leading-relaxed text-[#252528] outline-none ring-[#ff5212] placeholder:text-[#a7a7ae] focus:ring-2 disabled:opacity-50"
                  />
                </div>
              ) : null}
            </div>
          )}
          {place.matchReason ? (
            <p className="px-2 font-manrope text-xs leading-relaxed text-amber-900">{place.matchReason}</p>
          ) : null}
          {!socialImportPlaceRows && place.canFindPlace && onFindPlace ? (
            <button
              type="button"
              className="px-2 font-manrope text-xs font-semibold text-[#ff5212] disabled:opacity-40"
              disabled={isBusy}
              onClick={() => onFindPlace(place.id)}
            >
              {place.findPlaceLabel || 'Find this place'}
            </button>
          ) : null}
          {!socialImportPlaceRows && place.sourceQuote ? (
            <details className="group px-2">
              <summary className="cursor-pointer list-none font-manrope text-xs font-medium text-[#525257] marker:content-none [&::-webkit-details-marker]:hidden">
                <span className="underline decoration-[#a7a7ae] underline-offset-2 group-open:no-underline">
                  {sourceExcerptLabel}
                </span>
              </summary>
              <p className="mt-1 whitespace-pre-wrap font-manrope text-xs leading-relaxed text-[#525257]">
                {place.sourceQuote}
              </p>
            </details>
          ) : null}
        </div>
      );
    },
    [
      expandedPlaceIds,
      isBusy,
      onFindPlace,
      onPlaceNotesChange,
      onTogglePlace,
      selectedIds,
      socialImportPlaceRows,
      sourceExcerptLabel,
      togglePlaceDetails,
    ],
  );

  if (isImportDocs) {
    return (
      <div className={`flex flex-col gap-5 pb-1 ${className}`.trim()}>
        {sections.map((section) => (
          <div key={section.key} className="flex flex-col gap-3">
            {section.heading ? (
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-lora text-base font-semibold tracking-[0.3px] text-[#252528]">
                  {section.heading}
                </h3>
                {section.subheading ? (
                  <p className="font-manrope text-[13px] text-[#525257]">{section.subheading}</p>
                ) : null}
              </div>
            ) : null}
            {section.description ? (
              <p className="font-manrope text-[13px] leading-normal text-[#525257]">{section.description}</p>
            ) : null}
            <div className="flex flex-col gap-3">
              {section.places.map((place) => renderImportDocsPlaceRow(place))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`flex min-h-0 flex-1 flex-col gap-4 overflow-hidden ${className}`.trim()}>
      <div className="shrink-0 overflow-hidden rounded-[10px] border border-[#a7a7ae] bg-white">
        <div
          role="region"
          aria-label={`Suggested title: ${title || previewLabel}`}
          className={`flex w-full items-stretch gap-0 bg-white text-left ${isBusy ? 'opacity-50' : ''}`}
        >
          <div className="relative min-h-full w-[96px] min-w-[96px] shrink-0 overflow-hidden self-stretch">
            <CustomImage
              src={coverImageUrl ?? undefined}
              fallbackSources={fallbackCoverUrls.filter(
                (url): url is string => typeof url === 'string' && url.trim().length > 0,
              )}
              alt=""
              fill
              sizes="96px"
              fallbackSrc="/images/default_post.png"
              style={{ objectFit: 'cover', objectPosition: 'center' }}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 px-4 py-4">
            <p className="truncate font-manrope text-xs font-normal tracking-[0.24px] text-[#525257]">
              {previewLabel}
            </p>
            {previewBadge ? (
              <span className="self-start rounded-full bg-[#e7e7ff] px-2 pb-[2px] font-manrope text-xs font-normal tracking-[0.24px] text-[#525257]">
                {previewBadge}
              </span>
            ) : null}
            <label className="sr-only" htmlFor="social-import-review-title">
              {titleInputLabel}
            </label>
            <input
              id="social-import-review-title"
              type="text"
              value={title}
              onChange={(event) => onTitleChange(event.target.value)}
              disabled={isBusy}
              maxLength={120}
              className="w-full rounded-input border border-[#e1e1e7] bg-white px-3 py-2 font-manrope text-base font-semibold leading-[22px] tracking-[0.32px] text-[#252528] outline-none focus:border-[#252528] disabled:opacity-50"
              placeholder="Name your trip or list"
            />
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <span className="relative inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-[0_4px_12px_rgba(0,0,0,0.12)]">
          <span
            className="pointer-events-none absolute inset-[-2px] rounded-full opacity-75 blur-[4px]"
            style={{
              background:
                'conic-gradient(from 90deg, #ffc4a2 -0.86%, #e3f0ad 32.2%, #dcded6 49.55%, #d6ccff 66.9%, #eac8d1 83.02%, #ffc4a2 99.14%, #e3f0ad 132.2%)',
            }}
          />
          <img src={inspoSpotsIcon} alt="" className="relative h-4 w-4" />
        </span>
        <p className="font-lora text-[18px] font-semibold tracking-[0.5px] text-[#252528]">
          <span className="text-orange-500">
            {totalPlaceCount} {totalPlaceCount === 1 ? 'Place' : 'Places'}
          </span>{' '}
          Found
        </p>
      </div>

      <div className="flex shrink-0">
        <button
          type="button"
          onClick={onSelectAllToggle}
          disabled={isBusy || selectableIds.length === 0}
          className="min-h-[44px] rounded-[12px] border border-[#252528] px-4 font-manrope text-sm font-normal tracking-[0.5px] text-[#252528] shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {allSelectableSelected ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      <div ref={spotsScrollRef} className="gt-keyboard-scroll min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
        <div className="space-y-2 pb-1">
          {sections.map((section) => (
            <div key={section.key} className="flex flex-col gap-2">
              {section.heading ? (
                <h3 className="font-manrope text-sm font-semibold text-[#252528]">{section.heading}</h3>
              ) : null}
              {section.description ? (
                <p className="font-manrope text-xs leading-relaxed text-[#525257]">{section.description}</p>
              ) : null}
              {section.places.map((place) => renderPlaceRow(place))}
            </div>
          ))}
        </div>
        <div
          aria-hidden
          className={`pointer-events-none sticky bottom-0 z-[2] mt-[-44px] flex h-[44px] items-end justify-center gap-1 bg-gradient-to-b from-transparent via-white/90 to-white pb-2 transition-opacity ${
            showSpotsScrollHint ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#6e6e73' }} />
          <span className="font-manrope text-xs font-medium text-[#525257]">Scroll for more</span>
        </div>
        <div ref={spotsScrollSentinelRef} className="h-px w-full shrink-0" aria-hidden />
      </div>
    </div>
  );
}
